/**
 * Shared "where should we fetch weather for?" picker state.
 *
 * One source of truth so the full Weather page and the FarmerHome
 * weather chip stay in sync: pick Lagos on the chip, open Weather,
 * see Lagos. Pick Kano on Weather, go back home, chip reads Kano.
 *
 * ## Shapes
 *
 * A **location** is `{ lat, lon, name, kind }`:
 *   - `kind: 'geo'`  — browser geolocation (user granted permission)
 *   - `kind: 'plot'` — one of the user's farm plots (name = plot.name)
 *   - `kind: 'state'` — a Nigerian state (name = state label)
 *   - `kind: 'default'` — Lagos fallback
 *
 * A **choice** (what we persist) is `{ kind, plotId?, state? }` — just
 * enough to re-resolve to a `{lat, lon, name}` next time the user
 * opens the page. We never persist raw coords: plot coords might
 * change if a farmer edits the plot, and we want the picker to
 * follow that edit automatically.
 *
 * The one exception is `kind: 'geo'` — we don't persist that at all
 * because geolocation needs a user gesture every time (and the saved
 * coords would be stale within a day anyway).
 *
 * ## Resolution order
 *
 * The default-when-nothing-saved order per the PR 16 handover:
 *   1. First plot — its explicit lat/lon if set, else its `location`
 *      field resolved as a state name.
 *   2. User's profile state — covers distributors/manufacturers who
 *      declared coverage states but have no plots.
 *   3. Lagos.
 */
import { NIGERIA_STATE_CENTERS, centerOfState } from '@/data/nigeriaStateCenters';

export const LAGOS_DEFAULT = Object.freeze({
  lat: 6.5244,
  lon: 3.3792,
  name: 'Lagos, Nigeria',
  kind: 'default',
});

const STORAGE_PREFIX = 'af_weather_location.';

function _storageKey(user) {
  const id = user?.id || 'anon';
  return `${STORAGE_PREFIX}${id}`;
}

/**
 * Read the saved choice for this user. Returns null on empty, bad
 * JSON, or no-such-storage (private window, Safari with cookies
 * blocked, etc.) — callers always have the default-resolution path.
 */
export function readSavedChoice(user) {
  try {
    const raw = window.localStorage.getItem(_storageKey(user));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.kind === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist a choice. Silently swallows storage errors — the picker UI
 * still works (in-memory state) even when the browser refuses to
 * write; the preference just won't survive a reload.
 */
export function writeSavedChoice(user, choice) {
  if (!choice || typeof choice !== 'object') return;
  // Geolocation isn't worth saving — it has to be re-requested each
  // time with a user gesture anyway.
  if (choice.kind === 'geo') return;
  try {
    window.localStorage.setItem(_storageKey(user), JSON.stringify(choice));
  } catch {
    /* ignore */
  }
}

export function clearSavedChoice(user) {
  try {
    window.localStorage.removeItem(_storageKey(user));
  } catch {
    /* ignore */
  }
}

/**
 * Turn a plot row into a `{lat, lon, name}` or null if it has no
 * usable coords. Order:
 *   1. Explicit numeric latitude + longitude on the plot.
 *   2. Plot.location interpreted as a Nigerian state name.
 */
export function locationFromPlot(plot) {
  if (!plot) return null;
  const lat = Number(plot.latitude);
  const lon = Number(plot.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0)) {
    return {
      lat,
      lon,
      name: plot.name || plot.crop_type || 'Plot',
      kind: 'plot',
      plotId: plot.id,
    };
  }
  const stateMatch = centerOfState(plot.location);
  if (stateMatch) {
    return {
      lat: stateMatch.lat,
      lon: stateMatch.lon,
      name: plot.name || plot.location,
      kind: 'plot',
      plotId: plot.id,
    };
  }
  return null;
}

/**
 * Resolve a stored choice back into a usable location, pulling fresh
 * data from the latest plot list. Returns null if the saved choice
 * can no longer be honoured (plot deleted, state renamed) so the
 * caller falls back to the default chain.
 */
export function resolveChoice(choice, plots) {
  if (!choice) return null;
  if (choice.kind === 'state') {
    const c = centerOfState(choice.state);
    if (!c) return null;
    return { lat: c.lat, lon: c.lon, name: choice.state, kind: 'state', state: choice.state };
  }
  if (choice.kind === 'plot') {
    const plot = (plots || []).find((p) => p?.id === choice.plotId);
    if (!plot) return null;
    return locationFromPlot(plot);
  }
  if (choice.kind === 'default') {
    return { ...LAGOS_DEFAULT };
  }
  return null;
}

/**
 * The default-when-nothing-saved resolution chain. Pure — no
 * localStorage. Always returns a usable location (Lagos last-ditch).
 */
export function defaultLocation(user, plots) {
  if (Array.isArray(plots)) {
    for (const p of plots) {
      const loc = locationFromPlot(p);
      if (loc) return loc;
    }
  }
  // Coverage states on distributor/manufacturer profiles.
  const profileState = user?.states?.[0];
  if (profileState) {
    const c = centerOfState(profileState);
    if (c) {
      return { lat: c.lat, lon: c.lon, name: profileState, kind: 'state', state: profileState };
    }
  }
  return { ...LAGOS_DEFAULT };
}

/**
 * Top-level helper for consumers: read the saved choice (if any) and
 * resolve it, otherwise return the default-chain pick. Guaranteed
 * non-null `{lat, lon, name, kind}`.
 */
export function getSavedWeatherLocation(user, plots) {
  const choice = readSavedChoice(user);
  if (choice) {
    const resolved = resolveChoice(choice, plots);
    if (resolved) return resolved;
  }
  return defaultLocation(user, plots);
}

/**
 * Build the full option list for the picker UI. One flat array in
 * display order so the dropdown can map over it without caring about
 * section headers.
 */
export function buildPickerOptions(plots) {
  const opts = [];
  opts.push({ id: 'geo', kind: 'geo', labelKey: 'weather.picker.myLocation' });
  (plots || []).forEach((p) => {
    const loc = locationFromPlot(p);
    if (!loc) return;
    opts.push({
      id: `plot:${p.id}`,
      kind: 'plot',
      plotId: p.id,
      label: p.name || p.location || 'Plot',
    });
  });
  Object.keys(NIGERIA_STATE_CENTERS)
    .filter((k) => k !== 'FCT')
    .sort((a, b) => a.localeCompare(b))
    .forEach((state) => {
      opts.push({ id: `state:${state}`, kind: 'state', state, label: state });
    });
  return opts;
}
